/**
 * router/index.js
 *
 * Vue Router 4 setup + auth guard global untuk Portal IHC.
 *
 * Fungsi:
 * - createRouter dengan history mode (clean URL tanpa hash)
 * - scroll behavior: kembali ke saved position (back/forward) atau ke top
 * - beforeEach: auth guard
 *     • route butuh auth (meta.authRequired) tapi belum login → push ke /login
 *     • sudah login tapi akses /login /register /forgot-password → push ke /
 * - beforeResolve: update document.title sesuai meta.title route
 *
 * API: TIDAK ADA langsung. Hanya membaca getter `auth/loggedIn` dari Vuex.
 *
 * Dependency:
 * - routes.js                        → daftar semua route
 * - app.config (appConfig.title)     → suffix browser tab title
 * - state/store (auth/loggedIn)      → cek user logged in atau tidak
 */
import { createWebHistory, createRouter } from "vue-router";
import routes from './routes';
import appConfig from "../../app.config";
import store from '@/state/store';

const router = createRouter({
  history: createWebHistory(),
  routes,
  mode: 'history',
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0, left: 0 };
    }
  },
});

/**
 * Auth guard global.
 *
 * Logic:
 * - kalau route butuh auth (meta.authRequired === true) tapi user belum login,
 *   push ke /login dengan query.redirectFrom = path tujuan awal
 * - kalau user sudah login dan akses public page (/login /register /forgot-password),
 *   push ke / (dashboard)
 * - selain itu, lanjutkan navigasi normal
 */
router.beforeEach((routeTo, routeFrom, next) => {
  const publicPages = ['/login', '/register', '/forgot-password'];
  const authRequired = routeTo.matched.some((route) => route.meta.authRequired);
  const isPublicPage = publicPages.includes(routeTo.path);

  const isLoggedIn = store.getters['auth/loggedIn'];

  // If route requires auth and user is not logged in
  if (authRequired && !isLoggedIn) {
    return next({ name: 'login', query: { redirectFrom: routeTo.fullPath } });
  }

  // If user is logged in and trying to access login page, redirect to dashboard
  if (isPublicPage && isLoggedIn) {
    return next({ name: 'default' });
  }

  next();
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  try {
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            if (args.length) {
              next(...args);
              reject(new Error('Redirected'));
            } else {
              resolve();
            }
          });
        } else {
          resolve();
        }
      });
    }
  } catch (error) {
    return;
  }
  document.title = routeTo.meta.title + ' | ' + appConfig.title;
  next();
});

export default router;
